// 📄 pages/Proyectos/components/ProveedorCard.tsx
// Tarjeta resumen de un proveedor en los resultados de "Buscar
// Proveedores" (ver BuscarProveedores.tsx) — los nombres de campo son tal
// cual los devuelve Ariba (con espacios, ej. "Supplier Name"), confirmados
// contra una respuesta real antes de codear, no adivinados.
import { Building2, MapPin, Mail, ChevronRight } from 'lucide-react';
import { Proveedor } from '../../../services/slpProveedoresService.ts';

interface ProveedorCardProps {
  proveedor: Proveedor;
  onVerDetalle: () => void;
}

// Paleta compartida por estado — cubre los valores más comunes vistos en
// Ariba SLP (Registration/Qualification Status, Blocked, Approved,
// Integrated to ERP); cualquier valor no listado cae al estilo neutro en
// vez de fallar.
const colorEstado = (valor?: string): string => {
  const v = (valor || '').toLowerCase();
  if (['registered', 'qualified', 'true', 'active'].includes(v)) return 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30';
  if (['inregistration', 'qualificationstarted', 'inqualification', 'pendingqualificationapproval', 'qualifiedforsome'].includes(v)) {
    return 'text-amber-300 bg-amber-500/10 border-amber-500/30';
  }
  if (['error', 'expired', 'disqualified', 'notqualified', 'rejected'].includes(v)) return 'text-red-300 bg-red-500/10 border-red-500/30';
  return 'text-gray-400 bg-white/5 border-white/20';
};

const esVerdadero = (valor?: string) => (valor || '').toLowerCase() === 'true';

export default function ProveedorCard({ proveedor, onVerDetalle }: ProveedorCardProps) {
  const nombre = proveedor['Supplier Name'] || 'Proveedor sin nombre';
  const smVendorId = proveedor['SM Vendor ID'];
  const erpVendorId = proveedor['ERP Vendor ID'];
  const ciudad = proveedor['Address - City'];
  const pais = proveedor['Address - Country Code'];
  const contactoEmail = proveedor['Primary contact email'];
  const bloqueado = esVerdadero(proveedor['Blocked Status']);
  const cantidadCalificaciones = Array.isArray(proveedor.qualifications) ? proveedor.qualifications.length : 0;

  return (
    <button
      type="button"
      onClick={onVerDetalle}
      className="text-left w-full bg-white/5 hover:bg-white/[0.07] border border-white/10 hover:border-indigo-500/40 rounded-2xl p-4 space-y-3 transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Building2 className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-white leading-snug truncate">{nombre}</h4>
            <p className="text-[11px] text-gray-500 font-mono truncate">
              {smVendorId}{erpVendorId ? ` · ERP ${erpVendorId}` : ''}
            </p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-indigo-400 flex-shrink-0 mt-1 transition-colors" />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {proveedor['Registration Status'] && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${colorEstado(proveedor['Registration Status'])}`}>
            {proveedor['Registration Status']}
          </span>
        )}
        {proveedor['Qualification Status'] && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${colorEstado(proveedor['Qualification Status'])}`}>
            {proveedor['Qualification Status']}
          </span>
        )}
        {bloqueado && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-red-300 bg-red-500/10 border-red-500/30">
            Bloqueado
          </span>
        )}
        {cantidadCalificaciones > 0 && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border text-gray-400 bg-white/5 border-white/10">
            {cantidadCalificaciones} calificación{cantidadCalificaciones !== 1 ? 'es' : ''}
          </span>
        )}
      </div>

      {(ciudad || contactoEmail) && (
        <div className="space-y-1 pt-1 border-t border-white/5">
          {ciudad && (
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <MapPin className="w-3 h-3 flex-shrink-0 text-gray-500" />
              {ciudad}{pais ? `, ${pais}` : ''}
            </p>
          )}
          {contactoEmail && (
            <p className="text-xs text-gray-400 flex items-center gap-1.5 truncate">
              <Mail className="w-3 h-3 flex-shrink-0 text-gray-500" />
              <span className="truncate">{contactoEmail}</span>
            </p>
          )}
        </div>
      )}
    </button>
  );
}
